import { useCallback, useMemo, useState } from 'react';

/**
 * Hook to manage the prioritize logic state.
 * It provides functions to enter, exit, and toggle prioritize mode.
 *
 * @returns isPrioritizing: Boolean indicating if prioritize mode is active.
 * @returns enterPrioritize: Function to enter prioritize mode.
 * @returns exitPrioritize: Function to exit prioritize mode.
 * @returns togglePrioritize: Function to toggle prioritize mode.
 **/

export const usePrioritizeLogic = () => {
	const [isPrioritizing, set] = useState(false);
	const enterPrioritize = useCallback(() => set(true), []);
	const exitPrioritize = useCallback(() => set(false), []);
	const togglePrioritize = useCallback(() => set((p) => !p), []);
	return { isPrioritizing, enterPrioritize, exitPrioritize, togglePrioritize };
};

/**
 * Hook to manage the order of rows during prioritize mode.
 *
 * @param getId Function to extract the unique identifier from a row.
 * @returns initFromRows: Function to initialize the order from a list of rows.
 * @returns indexOf: Function to get the current index of a row by its ID.
 * @returns moveUp: Function to move a row up in the order by its ID.
 * @returns moveDown: Function to move a row down in the order by its ID.
 * @returns setPosition: Function to set a row's position directly by its ID and new position.
 * @returns total: Function to get the total number of rows being prioritized.
 * @returns orderedIds: Array of IDs representing the current order of rows.
 * @returns orderedRows: Array of rows in their current prioritized order.
 * @returns hasChanges: Boolean indicating if the current order differs from the initial order.
 */

export const usePrioritizeOrder = <TRow,>(getId: (row: TRow) => string | number) => {
	const [ids, setIds] = useState<string[]>([]);
	const [snapshot, setSnapshot] = useState<TRow[]>([]);

	const indexMap = useMemo(() => {
		const m = new Map<string, number>();
		ids.forEach((id, i) => m.set(id, i));
		return m;
	}, [ids]);

	const initFromRows = useCallback(
		(rows: TRow[]) => {
			setSnapshot(rows);
			setIds(rows.map((r) => String(getId(r))));
		},
		[getId],
	);

	const indexOf = useCallback((id: string | number) => indexMap.get(String(id)) ?? -1, [indexMap]);

	const moveUp = useCallback(
		(id: string | number) => {
			setIds((prev) => {
				const s = String(id);
				const i = indexMap.get(s);
				if (i == null || i === 0) return prev;
				const next = prev.slice();
				if (next[i] !== undefined && next[i - 1] !== undefined) {
					[next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
				}
				return next;
			});
		},
		[indexMap],
	);

	const moveDown = useCallback(
		(id: string | number) => {
			setIds((prev) => {
				const s = String(id);
				const i = indexMap.get(s);
				if (i == null || i >= prev.length - 1) return prev;
				const next = prev.slice();
				if (next[i] !== undefined && next[i + 1] !== undefined) {
					[next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
				}
				return next;
			});
		},
		[indexMap],
	);

	const setPosition = useCallback(
		(id: string | number, pos1: number) => {
			setIds((prev) => {
				const s = String(id);
				const from = prev.findIndex((x) => x === s);
				if (from == null) return prev;
				const to = Math.max(0, Math.min(prev.length - 1, Math.floor(pos1) - 1));
				if (from === to) return prev;
				const next = prev.slice();
				const removed = next.splice(from, 1)[0];
				if (removed !== undefined) {
					next.splice(to, 0, removed);
				}
				return next;
			});
		},
		[indexMap],
	);

	const orderedRows = useMemo(() => {
		if (snapshot.length === 0 || ids.length === 0) return snapshot;
		const byId = new Map(snapshot.map((r) => [String(getId(r)), r]));
		const placed: TRow[] = [];
		for (const id of ids) {
			const r = byId.get(id);
			if (r) placed.push(r);
		}
		if (placed.length !== snapshot.length) {
			const missing = snapshot.filter(
				(r) => !byId.has(String(getId(r))) || !ids.includes(String(getId(r))),
			);
			return [...placed, ...missing];
		}
		return placed;
	}, [snapshot, ids, getId]);

	const total = useCallback(() => ids.length, [ids]);

	const hasChanges = useMemo(() => {
		if (snapshot.length !== ids.length) return true;
		return snapshot.some((row, i) => getId(row) !== ids[i]);
	}, [snapshot, ids]);

	return {
		initFromRows,
		indexOf,
		moveUp,
		moveDown,
		setPosition,
		total,
		orderedIds: ids,
		orderedRows,
		hasChanges,
	};
};
